import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { mitAnmeldeHinweis, rueckwegHolen, type AnmeldeHinweis } from '../../lib/anmeldeRueckweg';

/**
 * Fehler, den Supabase nach Google in die Adresse schreibt (?error=… oder
 * #error=…). Typisch: Google abgebrochen (access_denied) oder Supabase legt
 * das Konto nicht an — im geteilten Projekt sind neue Konten nur mit
 * @randori-pro.de erlaubt („Database error saving new user“).
 */
function fehlerAusAdresse(): AnmeldeHinweis | null {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const fehler = query.get('error') ?? hash.get('error');
  const text = query.get('error_description') ?? hash.get('error_description');
  if (!fehler && !text) return null;
  return fehler === 'access_denied' ? 'abgebrochen' : 'fehlgeschlagen';
}

/**
 * Supabase OAuth Callback-Route.
 * Nach Login leitet Google auf `/auth/callback?code=...` zurück.
 * Supabase tauscht den Code automatisch gegen eine Session —
 * wir warten hier und leiten weiter — zurück zum Feedback-Link, wenn die
 * Anmeldung dort begann (sessionStorage „mail.nachAnmeldung“), sonst auf „/“.
 * Scheitert die Anmeldung, geht es mit ?anmeldung=… zurück zum Link, damit
 * der Hinweis dort erklärt, was los ist (statt still im Kreis zu laufen).
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const fertig = useRef(false);

  useEffect(() => {
    // Rückweg nur einmal lesen (und löschen) — alle Wege unten nutzen ihn.
    const weiter = () => {
      if (fertig.current) return;
      fertig.current = true;
      navigate(rueckwegHolen(), { replace: true });
    };
    const gescheitert = (hinweis: AnmeldeHinweis) => {
      if (fertig.current) return;
      fertig.current = true;
      const pfad = rueckwegHolen();
      navigate(pfad === '/' ? '/login' : mitAnmeldeHinweis(pfad, hinweis), { replace: true });
    };

    if (!supabase) { navigate('/login', { replace: true }); return; }

    // Fehler gleich auswerten — eine Session kommt dann nicht mehr.
    const fehler = fehlerAusAdresse();
    if (fehler) { gescheitert(fehler); return; }

    // Nur weiterleiten, wenn wirklich eine Session da ist. INITIAL_SESSION
    // feuert auch mit session=null, waehrend der Code-Austausch noch laeuft —
    // ein zu fruehes navigate('/') warf den User faelschlich zurueck auf /login.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) weiter();
    });

    const timer = setTimeout(async () => {
      const { data } = await supabase!.auth.getSession();
      if (data.session) { weiter(); return; }
      // Keine Session und kein Fehler in der Adresse: trotzdem nicht geklappt.
      gescheitert('fehlgeschlagen');
    }, 3000);

    return () => { subscription.unsubscribe(); clearTimeout(timer); };
  }, [navigate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
      <div className="loading-spinner" />
      <p style={{ color: 'var(--text-secondary)' }}>Anmeldung wird abgeschlossen...</p>
    </div>
  );
}
